import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  formatDate,
  INDUSTRY_VOICES_THEME,
  SITE_URL,
} from '../../config';

function toMs(value) {
  if (!value) return 0;
  if (typeof value === 'string') return new Date(value).getTime() || 0;
  if (value?.toMillis) return value.toMillis();
  try {
    return new Date(value).getTime() || 0;
  } catch {
    return 0;
  }
}

function normaliseToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isIndustryVoices(post) {
  const want = normaliseToken(INDUSTRY_VOICES_THEME);
  const category = normaliseToken(post?.category);
  if (category === want) return true;
  const themes = Array.isArray(post?.themes) ? post.themes : [];
  return themes.some((t) => normaliseToken(t) === want);
}

function IndustryVoicesPage() {
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState(() => ({}));

  const markLoaded = (key) => {
    if (!key) return;
    setLoadedImages((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  };

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'), limit(300));
    const unsub = onSnapshot(
      q,
      (snap) => {
        // Avoid rendering stale cached snapshots on refresh (causes brief fallback-image flash)
        if (snap.metadata?.fromCache) return;
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPosts(items);
        setLoading(false);
      },
      () => {
        setPosts([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const voices = useMemo(() => {
    return [...posts]
      .filter((p) => p && p.published !== false)
      .filter(isIndustryVoices)
      .sort((a, b) => toMs(b.publishedAt) - toMs(a.publishedAt));
  }, [posts]);

  const canonicalUrl = `${SITE_URL}${location.pathname}`;

  return (
    <section aria-label="Industry voices">
      <Helmet>
        <title>Industry voices – IGAMINGSPOT</title>
        <meta
          name="description"
          content="Influencer and operator perspectives from the iGaming niche: interviews, opinion pieces, and commentary."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Industry voices – IGAMINGSPOT" />
        <meta
          property="og:description"
          content="Influencer and operator perspectives from the iGaming niche."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_URL}/logo512.png`} />
        <meta property="og:site_name" content="IGAMINGSPOT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industry voices – IGAMINGSPOT" />
        <meta
          name="twitter:description"
          content="Influencer and operator perspectives from the iGaming niche."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />
      </Helmet>

      <div className="content-card news-feed-card">
        <div className="content-card-header">
          <div>
            <div className="section-title">Theme</div>
            <h1 className="section-subtitle">{INDUSTRY_VOICES_THEME}</h1>
          </div>
          <div className="secondary-text">{voices.length} posts</div>
        </div>

        <p className="secondary-text" style={{ marginTop: 4 }}>
          Posts about influencers, founders, operators, and commentators in the iGaming niche.
        </p>

        {loading && !posts.length && (
          <p className="secondary-text" style={{ marginTop: 12 }}>
            Loading Industry Voices…
          </p>
        )}

        {!loading && !voices.length && (
          <p className="secondary-text" style={{ marginTop: 12 }}>
            No Industry Voices posts yet. Add the theme <strong>{INDUSTRY_VOICES_THEME}</strong> to a post in the admin editor.
          </p>
        )}

        {!!voices.length && (
          <div className="latest-block" style={{ marginTop: 18 }}>
            <div className="news-grid">
              {voices.map((article) => {
                const imgSrc = article.imageThumbUrl || article.imageUrl;
                const slug = article.slug;

                return (
                  <Link key={article.id || slug} to={`/news/${slug}`} itemProp="url">
                    <article className="news-tile">
                      {imgSrc && (
                        <div
                          className="news-tile-image-wrap"
                          data-loaded={loadedImages[`iv:${imgSrc}`] ? 'true' : 'false'}
                          style={{
                            '--lqip-bg': article.imageLqip ? `url("${article.imageLqip}")` : undefined,
                          }}
                        >
                          <img
                            key={imgSrc}
                            src={imgSrc}
                            alt={article.title}
                            className="news-tile-image"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            decoding="async"
                            onLoad={() => markLoaded(`iv:${imgSrc}`)}
                            onError={(e) => {
                              const target = e.target;
                              // eslint-disable-next-line no-param-reassign
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="news-tile-meta">
                        <span className="badge-pill">{article.region}</span>
                        <span className="dot" aria-hidden="true" />
                        <span className="secondary-text">
                          <time dateTime={String(article.publishedAt)}>{formatDate(article.publishedAt)}</time>
                        </span>
                      </div>
                      <h3 className="news-tile-title">{article.title}</h3>
                      <p className="news-tile-excerpt">{article.excerpt}</p>
                      <div className="news-tile-footer">
                        <span className="secondary-text">{article.category}</span>
                        <span className="dot" aria-hidden="true" />
                        <span className="secondary-text">{INDUSTRY_VOICES_THEME}</span>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default IndustryVoicesPage;



